import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getToken, logout } from '../services/AuthService';
import wsService from '../services/WebSocketService';
import audioService from '../services/AudioService';
import ChatDisplay from '../components/ChatDisplay';
import EditZone from '../components/EditZone';
import ZoomControls from '../components/ZoomControls';
import Sidebar from '../components/Sidebar';
import StatusIndicator from '../components/StatusIndicator';
import { Menu, RotateCw, PlusCircle, LogOut, Mic, MicOff, Paperclip, Send, ArrowUp } from 'lucide-react';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// New brand palette — LN gradient + Meta-blue primary, light surface
const GRAD = 'linear-gradient(100deg,#3B3CC4 0%,#7C2BD0 30%,#C81E73 55%,#E51E50 72%,#F26A2C 100%)';
const PRIMARY = '#0866FF';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#F7F9FC',
    color: '#1F2937',
    fontFamily: "'Hanken Grotesk', sans-serif",
  },

  /* ── Top Bar ── */
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 18px',
    background: GRAD,
    borderBottom: 'none',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  iconBtn: {
    background: 'rgba(255,255,255,0.16)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '7px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  brand: {
    fontSize: '17px',
    fontWeight: '800',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  spacer: { flex: 1 },

  timerBox: {
    fontSize: '12px',
    color: '#fff',
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.28)',
    borderRadius: '30px',
    padding: '5px 12px',
    fontWeight: '700',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
  },
  timerBoxWarning: {
    fontSize: '12px',
    color: '#fff',
    background: 'rgba(225,24,73,0.85)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '30px',
    padding: '5px 12px',
    fontWeight: '700',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
    animation: 'pulse 1s infinite',
  },
  chatCodeBox: {
    fontSize: '13px',
    color: '#fff',
    padding: '5px 12px',
    background: 'rgba(255,255,255,0.16)',
    borderRadius: '30px',
    border: '1px solid rgba(255,255,255,0.28)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
  topBtn: (bg) => ({
    padding: '6px 13px',
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
  }),

  /* ── Main area ── */
  mainArea: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  chatArea: {
    flex: 1,
    overflowY: 'auto',
    padding: 'clamp(12px, 3vw, 20px)',
  },

  /* ── Bottom section ── */
  bottomSection: {
    flexShrink: 0,
    background: '#fff',
    borderTop: '1px solid #E8EBF2',
  },

  /* Transcript row: text area + blue arrow button side by side */
  transcriptRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    padding: '10px 14px 6px 14px',
  },
  transcriptBar: {
    flex: 1,
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1F2937',
    minHeight: '36px',
    maxHeight: '67px',
    overflowY: 'auto',
    // editable textarea styling — blends into the bar
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    padding: 0,
    margin: 0,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    display: 'block',
  },
  placeholder: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: '13px',
  },
  arrowBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: PRIMARY,
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginBottom: '2px',
  },

  /* Control row: Record | Attachment | spacer | Send */
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 14px 10px 14px',
    borderTop: '1px solid #F1F4F9',
  },
  recBtn: (isRecording) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: isRecording ? 'rgba(239,68,68,0.1)' : '#fff',
    border: isRecording ? '1px solid rgba(239,68,68,0.4)' : '1px solid #E5E7EB',
    color: isRecording ? '#EF4444' : '#6B7280',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '8px',
    transition: 'all 0.2s',
  }),
  attachBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#fff',
    border: '1px solid #E5E7EB',
    color: '#6B7280',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '8px',
  },
  attachQueue: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '6px 0',
    marginBottom: '6px',
  },
  attachChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    padding: '4px 8px',
    fontSize: '12px',
    color: '#475569',
  },
  attachChipName: {
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '12px',
    color: '#6B7280',
  },
  attachChipRemove: {
    background: 'none',
    border: 'none',
    color: '#94A3B8',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '0 2px',
    lineHeight: 1,
  },
  sendBtn: (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 20px',
    background: GRAD,
    border: 'none',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'opacity 0.15s',
  }),


  idleWarningBanner: {
    padding: '10px 20px',
    background: '#FEF3C7',
    borderBottom: '1px solid #FCD34D',
    color: '#92400E',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexShrink: 0,
  },
  idleWarningBtn: {
    background: '#D97706',
    border: 'none',
    color: '#fff',
    padding: '4px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },

  errorBanner: {
    padding: '8px 16px',
    background: '#FEE2E2',
    borderBottom: '1px solid #FCA5A5',
    color: '#B91C1C',
    fontSize: '13px',
    textAlign: 'center',
    cursor: 'pointer',
  },
};

export default function Chat() {
  const { chatCode } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // 30-min idle session timeout
  const { showWarning: showIdleWarning, secondsLeft: idleSecondsLeft, resetTimer: resetIdleTimer } = useSessionTimeout();

  // Silent JWT refresh when < 30 min remain on token
  useTokenRefresh();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const saved = localStorage.getItem('copilot-zoom-level');
      if (saved !== null && !isNaN(Number(saved))) return Number(saved);
    } catch (e) {}
    return 100;
  });
  const [isRecording, setIsRecording] = useState(false);
  const [showEditZone, setShowEditZone] = useState(false);
  const [editText, setEditText] = useState('');
  const [transcript, setTranscript] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]); // queued files before Send
  const [status, setStatus] = useState('idle');
  const [recentSessions, setRecentSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  // Session timer
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionMode, setSessionMode] = useState('interview'); // 'interview' | 'general'

  // Safety valve: if isStreaming gets stuck for 30s, auto-reset
  useEffect(() => {
    if (!isStreaming) return;
    const timeout = setTimeout(() => {
      setIsStreaming(false);
      setStreamingContent('');
      setStatus('idle');
      console.warn('[Chat] isStreaming safety reset after 30s');
    }, 30000);
    return () => clearTimeout(timeout);
  }, [isStreaming]);
  // Session countdown timer — reads JWT expiry
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expMs = payload.exp * 1000;

      const tick = () => {
        const diff = expMs - Date.now();
        if (diff <= 0) {
          setTimeRemaining('Expired');
          logout();
          navigate('/');
        } else {
          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          setTimeRemaining(
            h > 0
              ? `${h}h ${String(m).padStart(2,'0')}m remaining`
              : `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} remaining`
          );
        }
      };

      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    } catch (e) {}
  }, []);


  const [wsConnected, setWsConnected] = useState(false);
  const [error, setError] = useState('');

  const wakeLockRef = useRef(null);
  const chatAreaRef = useRef(null);
  const transcriptBarRef = useRef(null);
  const accumulatedRef = useRef('');
  const transcriptFrozenRef = useRef(false); // blocks stale Deepgram callbacks after Send

  /* ── Wake Lock ── */
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (err) {}
    };
    requestWakeLock();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLockRef.current) wakeLockRef.current.release();
    };
  }, []);

  /* ── Zoom persistence ── */
  useEffect(() => {
    try { localStorage.setItem('copilot-zoom-level', String(zoomLevel)); } catch (e) {}
  }, [zoomLevel]);

  /* ── Audio callbacks ── */
  useEffect(() => {
    audioService.onTranscript(({ final: finalText, interim }) => {
      // Ignore in-flight transcripts that arrive after Send clears the bar
      if (transcriptFrozenRef.current) return;
      if (finalText) {
        accumulatedRef.current = accumulatedRef.current
          ? accumulatedRef.current + ' ' + finalText
          : finalText;
      }
      const display = accumulatedRef.current
        + (accumulatedRef.current && interim ? ' ' : '')
        + (interim || '');
      setTranscript(display);
    });

    audioService.onStatus((newStatus) => {
      if (newStatus === 'listening') setStatus('listening');
      else if (newStatus === 'stopped') setStatus('idle');
      else if (newStatus === 'error') {
        setStatus('idle');
        setError('Microphone / transcription unavailable. Check you are signed in and try again.');
      }
    });
  }, []);

  /* ── Load session + connect WS ── */
  useEffect(() => {
    const token = getToken();

    const loadSession = async () => {
      try {
        // ── Load session metadata ──
        const res = await fetch(`${API_URL}/api/sessions/${chatCode}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.mode) setSessionMode(data.mode);
          if (data.messages) setMessages(data.messages);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
      }
    };

    const loadSessions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRecentSessions(data);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }
    };

    loadSession();
    loadSessions();

    wsService.onConnected((payload) => { setWsConnected(true); setError(''); if (payload?.mode) setSessionMode(payload.mode); });

    wsService.onStreamStart(() => {
      setIsStreaming(true);
      setStreamingContent('');
      setStatus('thinking');
    });

    wsService.onStreamToken((token) => {
      setStreamingContent(prev => prev + token);
    });

    wsService.onStreamEnd((data) => {
      setIsStreaming(false);
      setStreamingContent('');
      setStatus(isRecording ? 'listening' : 'idle');
      setMessages(prev => [...prev, {
        type: 'ai',
        content: data.fullContent,
        timestamp: data.timestamp,
      }]);
    });

    wsService.onStatus((s) => setStatus(s));
    wsService.onError((errMsg) => { setError(errMsg); setIsStreaming(false); setStatus('idle'); });
    wsService.connect(chatCode, token);

    return () => { wsService.disconnect(); audioService.stop(); };
  }, [chatCode]);

  /* ── Scroll: auto-scroll transcript bar ── */
  useEffect(() => {
    if (transcriptBarRef.current) {
      transcriptBarRef.current.scrollTop = transcriptBarRef.current.scrollHeight;
    }
  }, [transcript]);

  /* ── Scroll position: restore on load ── */
  const initialScrollDone = useRef(false);
  useEffect(() => {
    if (!initialScrollDone.current && messages.length > 0 && chatAreaRef.current) {
      setTimeout(() => {
        const saved = localStorage.getItem(`chat-scroll-${chatCode}`);
        if (saved !== null) {
          chatAreaRef.current.scrollTop = Number(saved);
        } else {
          chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
        initialScrollDone.current = true;
      }, 150);
    }
  }, [messages, chatCode]);



  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        try { localStorage.setItem(`chat-scroll-${chatCode}`, String(el.scrollTop)); } catch (e) {}
      }, 200);
    };
    el.addEventListener('scroll', onScroll);
    return () => { clearTimeout(t); el.removeEventListener('scroll', onScroll); };
  }, [chatCode]);

  /* ── Send ── */
  const handleSend = useCallback(() => {
    if (isStreaming) return;

    let text;
    if (showEditZone) {
      text = [editText.trim(), transcript.trim()].filter(Boolean).join(' ');
    } else {
      text = transcript.trim();
    }

    // ── Freeze transcript bar immediately ────────────────────────────────────
    // Prevents in-flight Deepgram callbacks from re-populating the bar
    // while audioService restarts (race condition fix)
    transcriptFrozenRef.current = true;
    setTranscript('');
    setEditText('');
    accumulatedRef.current = '';
    setShowEditZone(false);

    const unfreezeAfterRestart = () => {
      if (isRecording) {
        // restart() only clears _buf — WebSocket stays open, no reconnect needed
        // 300ms covers any in-flight Deepgram messages queued before Send
        audioService.flush();
        setTimeout(() => { accumulatedRef.current = ''; setTranscript(''); transcriptFrozenRef.current = false; }, 800);
      } else {
        transcriptFrozenRef.current = false;
      }
    };

    // Send queued attachments first
    if (pendingAttachments.length > 0) {
      pendingAttachments.forEach(att => {
        if (att.type === 'image') {
          setMessages(prev => [...prev, {
            type: 'user',
            content: '[Image uploaded]',
            imageUrl: att.url,
            timestamp: new Date().toISOString(),
          }]);
          wsService.sendImage(text || 'Please explain this code screenshot.', att.url);
        } else {
          setMessages(prev => [...prev, {
            type: 'user',
            content: `📄 File uploaded: ${att.filename}`,
            timestamp: new Date().toISOString(),
          }]);
          wsService.sendTextFile(text || '', att.filename, att.fileContent);
        }
      });
      setPendingAttachments([]);
      if (isRecording) unfreezeAfterRestart();
      return;
    }

    if (!text) {
      transcriptFrozenRef.current = false;
      return;
    }

    setMessages(prev => [...prev, {
      type: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }]);

    wsService.sendTranscript(text);
    if (isRecording) unfreezeAfterRestart();
  }, [showEditZone, editText, transcript, isStreaming, isRecording, pendingAttachments]);

  /* ── Arrow button: copy transcript to edit zone ── */
  const handleArrowClick = () => {
    // Append the current transcript to the Edit Zone instead of overwriting,
    // so multiple pushes accumulate (each chunk on its own line).
    setEditText(prev => {
      const next = (transcript || '').trim();
      if (!next) return prev;
      return prev ? prev + '\n' + next : next;
    });
    setShowEditZone(true);
    setTranscript('');
    accumulatedRef.current = '';
    if (isRecording) audioService.flush();
  };

  /* ── Mic toggle ── */
  const toggleRecording = () => {
    if (!isRecording) {
      // Start recording — stays ON until page refresh
      audioService.start();
      setIsRecording(true);
      setStatus('listening');
    } else {
      // Stop recording
      audioService.stop();
      setIsRecording(false);
      setStatus('idle');
    }
  };

  /* ── Attachment ── */
  const handleAttach = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const token = getToken();
    const ext = file.name.split('.').pop().toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

    if (imageExts.includes(ext)) {
      const formData = new FormData();
      formData.append('image', file);
      try {
        const res = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (res.ok) {
          const fullUrl = `${API_URL}${data.imageUrl}`;
          // Queue it — do NOT send yet
          setPendingAttachments(prev => [...prev, {
            type: 'image',
            url: fullUrl,
            previewUrl: fullUrl,
            filename: file.name,
          }]);
        } else {
          setError(data.message || 'Upload failed');
        }
      } catch (err) {
        setError('Failed to upload image');
      }
    } else {
      const formData = new FormData();
      formData.append('textfile', file);
      try {
        const res = await fetch(`${API_URL}/api/upload/text`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (res.ok) {
          // Queue it — do NOT send yet
          setPendingAttachments(prev => [...prev, {
            type: 'text',
            filename: data.filename,
            fileContent: data.content,
          }]);
        } else {
          setError(data.message || 'Upload failed');
        }
      } catch (err) {
        setError('Failed to upload file');
      }
    }
    e.target.value = '';
  };

  /* ── Copy chat code ── */
  const copyChatCode = () => {
    navigator.clipboard.writeText(chatCode).catch(() => {});
  };

  /* ── Build display messages ── */
  const displayMessages = isStreaming
    ? [...messages, { type: 'ai', content: streamingContent, timestamp: new Date().toISOString(), isStreaming: true }]
    : messages;

  return (
    <div style={styles.container}>
      <style>{`@keyframes pulse { 0%,100%{ opacity:1; transform:scale(1); } 50%{ opacity:0.4; transform:scale(1.4); } }`}</style>

      {/* Idle Timeout Warning Banner */}
      {showIdleWarning && (
        <div style={styles.idleWarningBanner}>
          <span>⚠️ You've been idle — you'll be logged out in <strong>{idleSecondsLeft}s</strong> due to inactivity.</span>
          <button style={styles.idleWarningBtn} onClick={resetIdleTimer}>Stay logged in</button>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div style={styles.errorBanner} onClick={() => setError('')}>
          {error} (click to dismiss)
        </div>
      )}

      {/* ── Top Bar ── */}
      <div style={styles.topBar}>
        {/* Hamburger */}
        <button style={styles.iconBtn} onClick={() => setSidebarOpen(o => !o)}>
          <Menu size={20} />
        </button>

        {/* Brand */}
        <span style={styles.brand}>
          {sessionMode === 'general' ? '💬 General Chat' : (
            <>
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt=""
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
              />
              Lavan AI
            </>
          )}
        </span>

        {/* Zoom controls */}
        <ZoomControls zoomLevel={zoomLevel} onZoomChange={setZoomLevel} />

        {/* Chat code */}
        <div style={styles.chatCodeBox} onClick={copyChatCode} title="Click to copy">
          Chat Code: {chatCode}
          {wsConnected && <span style={{ color: '#22c55e', fontSize: '10px' }}>●</span>}
        </div>


        {/* Session Timer */}
        {timeRemaining && (
          <div style={timeRemaining.includes(':') && parseInt(timeRemaining) < 10 ? styles.timerBoxWarning : styles.timerBox}>
            ⏱ {timeRemaining}
          </div>
        )}

        {/* Reload Chat */}
        <button style={styles.topBtn('#dc2626')} onClick={() => { wsService.disconnect(); window.location.reload(); }}>
          <RotateCw size={13} /> Reload Chat
        </button>

        {/* New Interview / New Chat */}
        {sessionMode === 'general' ? (
          <button style={styles.topBtn('#16a34a')} onClick={() => { wsService.disconnect(); navigate('/general-chat'); }}>
            <PlusCircle size={13} /> New Chat
          </button>
        ) : (
          <button style={styles.topBtn('#16a34a')} onClick={() => { wsService.disconnect(); navigate('/dashboard'); }}>
            <PlusCircle size={13} /> New Interview
          </button>
        )}

        {/* Logout */}
        <button style={styles.topBtn('#dc2626')} onClick={() => { wsService.disconnect(); logout(); navigate('/'); }}>
          <LogOut size={13} /> Logout
        </button>
      </div>

      {/* ── Main Area ── */}
      <div style={styles.mainArea}>
        <div ref={chatAreaRef} style={styles.chatArea}>
          <StatusIndicator status={status} />
          <ChatDisplay messages={displayMessages} zoomLevel={zoomLevel} />
        </div>

        {/* ── Bottom Section ── */}
        <div style={styles.bottomSection}>

          {/* Edit Zone — appears above transcript bar when arrow clicked */}
          {showEditZone && (
            <EditZone
              text={editText}
              onChange={setEditText}
              onClose={() => setShowEditZone(false)}
            />
          )}

          {/* Pending Attachments Queue */}
          {pendingAttachments.length > 0 && (
            <div style={styles.attachQueue}>
              {pendingAttachments.map((att, i) => (
                <div key={i} style={styles.attachChip}>
                  {att.type === 'image' ? (
                    <img src={att.previewUrl} alt={att.filename}
                      style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} />
                  ) : (
                    <span style={{ fontSize: '20px' }}>📄</span>
                  )}
                  <span style={styles.attachChipName}>{att.filename}</span>
                  <button style={styles.attachChipRemove}
                    onClick={() => setPendingAttachments(prev => prev.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Transcript Bar + Arrow Button */}
          <div style={styles.transcriptRow}>
            <textarea
              ref={transcriptBarRef}
              style={styles.transcriptBar}
              value={transcript}
              onChange={(e) => { setTranscript(e.target.value); accumulatedRef.current = e.target.value; }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={isRecording ? '🎙 Listening… (or type here)' : 'Type your question, or click Record to speak…'}
              rows={1}
            />
            <button style={styles.arrowBtn} onClick={handleArrowClick} title="Edit transcript">
              <ArrowUp size={18} />
            </button>
          </div>

          {/* Control Row: spacer | Record | Attachment | Send */}
          <div style={styles.controlRow}>

            {/* Spacer pushes all buttons to the right */}
            <div style={{ flex: 1 }} />

            {/* Record — click to start, stays ON until page refresh */}
            <button style={styles.recBtn(isRecording)} onClick={toggleRecording} title={isRecording ? 'Click to stop recording' : 'Click to start recording'}>
              {isRecording ? (
                <>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1.2s ease-in-out infinite' }} />
                  <Mic size={16} />
                  <span>Recording</span>
                </>
              ) : (
                <><Mic size={16} /><span>Record</span></>
              )}
            </button>

            {/* Attachment */}
            <button style={styles.attachBtn} onClick={handleAttach}>
              <Paperclip size={16} /><span>Attachment</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,.txt,.md,.csv,.json,.js,.jsx,.ts,.tsx,.py,.java,.html,.css,.xml,.log,.sql,.yaml,.yml"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />

            {/* Send — disabled ONLY while streaming */}
            <button
              style={styles.sendBtn(isStreaming)}
              onClick={handleSend}
            >
              <Send size={16} /><span>Send</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar — overlays chat, close via X button */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sessions={recentSessions}
      />
    </div>
  );
}




